// usePostInteractions.ts

import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { apolloClient } from '../api/apolloClient';
import {
  BOOKMARK_POST_MUTATION,
  UNBOOKMARK_POST_MUTATION,
} from '../graphql/resolvers/postMutations/bookmarkPost.gql';
import {
  LIKE_POST_MUTATION,
  UNLIKE_POST_MUTATION,
} from '../graphql/resolvers/postMutations/likePost.gql';
import { FeedPost } from '../interfaces/feed.interface';

/**
 * Write a partial patch into Post:{postId}.viewerInteractions in the Apollo
 * cache. Fires for any cached Post:{id}; after TRIL-177 the feed posts are
 * normalized in the cache, so this covers every feed-card and post-detail
 * viewer.
 */
function writeViewerInteraction(
  postId: string,
  patch: {
    hasLiked?: boolean;
    likeCount?: number;
    hasBookmarked?: boolean;
    bookmarkCount?: number;
  },
) {
  apolloClient.cache.modify({
    id: apolloClient.cache.identify({ __typename: 'Post', id: postId }),
    fields: {
      viewerInteractions(existing) {
        if (!existing) return existing; // viewerInteractions is null on this cached post — nothing to patch
        return { ...existing, ...patch };
      },
    },
  });
}

/**
 * Toggles like / bookmark with optimistic updates and server reconciliation.
 * After TRIL-177 the feed reads from the Apollo cache (Post:{id}) and after
 * TRIL-178 this hook writes only the cache — `writeViewerInteraction` is the
 * single source of truth. No Redux dispatches; out-of-scope dispatchers
 * (UserFeed / UserInteractionFeed) still maintain their own feedStore state
 * until Slice 5.
 */
export function usePostInteractions() {
  const [likePost] = useMutation(LIKE_POST_MUTATION);
  const [unlikePost] = useMutation(UNLIKE_POST_MUTATION);
  const [bookmarkPost] = useMutation(BOOKMARK_POST_MUTATION);
  const [unbookmarkPost] = useMutation(UNBOOKMARK_POST_MUTATION);

  // ---------- LIKE ----------

  const toggleLike = useCallback(
    async (post: FeedPost): Promise<boolean> => {
      const postId = post.postId ?? post.id;
      if (!postId) return false;

      const prevHasLiked = !!post.viewerInteractions?.hasLiked;
      const prevLikeCount = post.viewerInteractions?.likeCount ?? 0;

      const nextHasLiked = !prevHasLiked;
      const nextLikeCount = Math.max(
        0,
        prevLikeCount + (nextHasLiked ? 1 : -1),
      );

      // 1) optimistic update — Apollo cache only (TRIL-178)
      writeViewerInteraction(postId, {
        hasLiked: nextHasLiked,
        likeCount: nextLikeCount,
      });

      const mutation = nextHasLiked ? likePost : unlikePost;

      try {
        const { data } = await mutation({ variables: { input: { postId } } });
        // TRIL-173 SP2a: transitional — data is either LikePostMutation or UnlikePostMutation
        type LikePayload = {
          likePost?: { likeCount: number };
          unlikePost?: { likeCount: number };
        };
        const d = data as unknown as LikePayload;
        const payload = d?.likePost ?? d?.unlikePost;
        if (!payload) return true;

        // 2) reconcile with server count — Apollo cache
        writeViewerInteraction(postId, { likeCount: payload.likeCount });
        return true;
      } catch (err) {
        console.error('Toggle like failed', err);

        // 3) rollback — Apollo cache
        writeViewerInteraction(postId, {
          hasLiked: prevHasLiked,
          likeCount: prevLikeCount,
        });
        return false;
      }
    },
    [likePost, unlikePost],
  );

  // ---------- BOOKMARK ----------

  const toggleBookmark = useCallback(
    async (post: FeedPost): Promise<boolean> => {
      const postId = post.postId ?? post.id;
      if (!postId) return false;

      const prevHasBookmarked = !!post.viewerInteractions?.hasBookmarked;
      const prevBookmarkCount = post.viewerInteractions?.bookmarkCount ?? 0;

      const nextHasBookmarked = !prevHasBookmarked;
      const nextBookmarkCount = Math.max(
        0,
        prevBookmarkCount + (nextHasBookmarked ? 1 : -1),
      );

      // 1) optimistic update — Apollo cache only (TRIL-178)
      writeViewerInteraction(postId, {
        hasBookmarked: nextHasBookmarked,
        bookmarkCount: nextBookmarkCount,
      });

      const mutation = nextHasBookmarked ? bookmarkPost : unbookmarkPost;

      try {
        const { data } = await mutation({
          variables: { input: { postId } },
        });
        // TRIL-173 SP2a: transitional — data is either BookmarkPostMutation or UnbookmarkPostMutation
        type BookmarkPayload = {
          bookmarkPost?: { bookmarkCount: number };
          unbookmarkPost?: { bookmarkCount: number };
        };
        const d = data as unknown as BookmarkPayload;
        const payload = d?.bookmarkPost ?? d?.unbookmarkPost;
        if (!payload) return true;

        // 2) reconcile with server count — Apollo cache
        writeViewerInteraction(postId, {
          bookmarkCount: payload.bookmarkCount,
        });
        return true;
      } catch (err) {
        console.error('Toggle bookmark failed', err);

        // 3) rollback — Apollo cache
        writeViewerInteraction(postId, {
          hasBookmarked: prevHasBookmarked,
          bookmarkCount: prevBookmarkCount,
        });
        return false;
      }
    },
    [bookmarkPost, unbookmarkPost],
  );

  return { toggleLike, toggleBookmark };
}
